import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { offerContractActions } from './offer-contract.actions';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { OfferingService } from '../../data/offering.service';
import { MessageService } from '@letly/ui-common';
import { ToastSeverity } from '@letly/models';
import { Router } from '@angular/router';
import { EMPTY, of } from 'rxjs';
import { offeringActions } from '../../data/offering.actions';

@Injectable()
export class OfferContractEffects {
  private actions$ = inject(Actions);
  private api = inject(OfferingService);
  private message = inject(MessageService);
  private router = inject(Router);

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offerContractActions.loadContract),
      mergeMap(({ offerId, contractId, includeSignatures }) =>
        this.api.getOfferContract(offerId, contractId, includeSignatures).pipe(
          map(res => {
            if (res.success) {
              return offerContractActions.onLoadContract({
                offerId,
                contractId,
                status: 'success',
                message: null,
                contract: res.data?.contract ?? null
              });
            }
            this.message.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
            return offerContractActions.onLoadContract({
              offerId,
              contractId,
              status: 'error',
              message: res.niceErrorMessage,
              contract: null
            });
          })
        )
      )
    )
  );

  createDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offerContractActions.createDraft),
      mergeMap(({ offerId, propertyId, contractId }) =>
        this.api.createDraft(offerId, contractId).pipe(
          map(res => {
            if (res.success) {
              const draftId = res.data['contract']?.id;
              if (draftId) {
                const currentUrl = this.router.url;
                if (currentUrl.includes('/landlord/property-detail')) {
                  this.router.navigate(['/landlord', 'property-detail', propertyId, 'offers', offerId, 'contract', draftId]);
                } else {
                  this.router.navigate(['/landlord', 'offers', offerId, 'contract', draftId]);
                }
              }
              return offerContractActions.onCreateDraft({
                offerId,
                contractId,
                status: 'success',
                message: null,
                contract: res.data['contract']
              });
            } else {
              this.message.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return offerContractActions.onCreateDraft({
                offerId,
                contractId,
                status: 'error',
                message: res.niceErrorMessage,
                contract: null
              });
            }
          })
        )
      )
    )
  );

  submit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offerContractActions.submit),
      mergeMap(({ offerId, contractId, propertyId }) =>
        this.api.submit(offerId, contractId).pipe(
          map(res => {
            if (res.success) {
              this.message.showSnackbar($localize`:@@offering.contracts.toast.draft-submit.success:Draft contract submitted.`, ToastSeverity.Success);
              const currentUrl = this.router.url;
              if (currentUrl.includes('/landlord/property-detail')) {
                this.router.navigate(['/landlord', 'property-detail', propertyId, 'offers', offerId]);
              } else {
                this.router.navigate(['/landlord', 'offers', propertyId, offerId]);
              }
              return offerContractActions.onSubmit({
                offerId,
                contractId,
                status: 'success',
                message: null,
                contract: res.data
              });
            } else {
              this.message.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return offerContractActions.onSubmit({
                offerId,
                contractId,
                status: 'error',
                message: res.niceErrorMessage,
                contract: null
              });
            }
          })
        )
      )
    )
  );

  saveDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offerContractActions.saveDraft),
      mergeMap(({ offerId, contractId, clauses }) =>
        this.api.updateDraft(offerId, contractId, clauses).pipe(
          map(res => {
            if (res.success) {
              this.message.showSnackbar($localize`:@@offering.contracts.toast.draft-saved:Draft saved.`, ToastSeverity.Success);
              return offerContractActions.onSaveDraft({
                offerId,
                contractId,
                status: 'success',
                message: null,
                contract: res.data['contract']
              });
            }

            this.message.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
            return offerContractActions.onSaveDraft({
              offerId,
              contractId,
              status: 'error',
              message: res.niceErrorMessage,
              contract: null
            });
          })
        )
      )
    )
  );

  updateDraftAndSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offerContractActions.updateDraftAndSubmit),
      mergeMap(({ offerId, contractId, propertyId, clauses }) =>
        this.api.updateDraft(offerId, contractId, clauses).pipe(
          mergeMap(updateRes => {
            if (updateRes.success) {
              return this.api.submit(offerId, contractId).pipe(
                switchMap(submitRes => {
                  if (submitRes.success) {
                    this.message.showSnackbar($localize`:@@offering.contracts.toast.submit.success:Contract submitted.`, ToastSeverity.Success);
                    const currentUrl = this.router.url;
                    if (currentUrl.includes('/landlord/property-detail')) {
                      this.router.navigate(['/landlord', 'property-detail', propertyId, 'offers', offerId]);
                    } else {
                      this.router.navigate(['/landlord', 'offers', offerId]);
                    }
                    return [
                      offerContractActions.onSubmit({
                        offerId,
                        contractId,
                        status: 'success',
                        message: null,
                        contract: submitRes.data['contract']
                      }),
                      offeringActions.loadLandlordOffers({ propertyId, append: false })
                    ];
                  } else {
                    this.message.showSnackbar(submitRes.niceErrorMessage, ToastSeverity.Error);
                    return [
                      offerContractActions.onSubmit({
                        offerId,
                        contractId,
                        status: 'error',
                        message: submitRes.niceErrorMessage,
                        contract: null
                      })
                    ];
                  }
                })
              );
            } else {
              this.message.showSnackbar(updateRes.niceErrorMessage, ToastSeverity.Error);
              return of(offerContractActions.onSubmit({
                offerId,
                contractId,
                status: 'error',
                message: updateRes.niceErrorMessage,
                contract: null
              }));
            }
          })
        )
      )
    )
  );

  acceptContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offerContractActions.acceptContract),
      mergeMap(({ offerId, contractId }) =>
        this.api.acceptContract(offerId, contractId).pipe(
          map(res => {
            if (res.success) {
              this.message.showSnackbar($localize`:@@offering.contracts.toast.accept.success:Contract accepted.`, ToastSeverity.Success);
              return offerContractActions.onAcceptContract({
                offerId,
                contractId,
                status: 'success',
                message: null
              });
            } else {
              this.message.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return offerContractActions.onAcceptContract({
                offerId,
                contractId,
                status: 'error',
                message: res.niceErrorMessage
              });
            }
          })
        )
      )
    )
  );

  onAcceptContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offerContractActions.onAcceptContract),
      switchMap(({ offerId, status }) => {
        if (status === 'success') {
          return [
            offeringActions.loadOfferDetail({ id: offerId }),
            offeringActions.loadTenantOffers({ append: false })
          ];
        }
        return EMPTY;
      })
    )
  );

}
